import { FormControl, FormHelperText, Autocomplete, TextField, Typography, createFilterOptions, FilterOptionsState, InputAdornment, alpha } from '@mui/material';
import { useEffect, useState, useCallback, useMemo } from 'react';
import colors from "@/utils/colors";
import { sfUIDisplayLight, sfUIDisplayMedium } from "@/utils/fonts";
import UIIcon from "../../icons/UIIcon";
import { iconName } from '@/utils/icons';
import UiArrowIcon from '@/components/icons/UIArrowIcon';
import Image from 'next/image';
interface Option {
  id: string | number;
  value: string;
  disabled?: boolean;
}

interface UISelectSearchProps {
  label: string;
  value: string;
  options: Option[];
  onChange?: (value: string, id: string | number) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  warning?: boolean;
  error?: boolean;
  errorText?: string;
  placeholder?: string;
  required?: boolean;
  maxOptions?: number;
  isAlta?: boolean;
  creatable?: boolean;
}

// Constantes para estilos reutilizables
const COMPONENT_HEIGHT = '44px';
const OPTION_MIN_HEIGHT = '36px';
const LISTBOX_MAX_HEIGHT = '256px';

const labelStyles = {
  color: colors.gray700,
  fontFamily: sfUIDisplayLight,
  fontWeight: "500",
  fontSize: 14,
  marginBottom: "5px"
} as const;

const popupIconStyles = {
  display: "flex",
  alignItems: "center",
  transition: 'transform 0.2s',
} as const;

const UISelectSearch = ({
  label,
  value,
  options,
  onChange,
  size = 'medium',
  disabled = false,
  error = false,
  errorText = '',
  placeholder = '',
  required = false,
  maxOptions,
  isAlta = false,
  creatable = false,
  warning = false,
}: UISelectSearchProps) => {
  const [localOptions, setLocalOptions] = useState<Option[]>(options);

  useEffect(() => {
    if (creatable) {
      // Si es creatable, mantener las opciones creadas por el usuario
      setLocalOptions((prev) => {
        // Filtrar las opciones creadas por el usuario (que no están en las opciones originales)
        const userCreatedOptions = prev.filter(
          prevOpt => !options.some(opt => opt.id === prevOpt.id)
        );
        // Combinar las opciones de props con las creadas por el usuario
        return [...options, ...userCreatedOptions];
      });
    } else {
      // Si no es creatable, simplemente reemplazar con las nuevas opciones
      setLocalOptions(options);
    }
  }, [options, creatable]);

  const filteredOptions = useMemo(() => {
    const limit = maxOptions ?? localOptions.length;
    return localOptions.slice(0, limit);
  }, [localOptions, maxOptions]);

  const selectedOption = useMemo(() =>
    localOptions.find(option => option.value === value || option.id === value),
    [localOptions, value]
  );

  const [_value, setValue] = useState<Option | null>(selectedOption || null);
  const [open, setOpen] = useState(false);

  const handleChange = useCallback((newValue: Option | null) => {
    if (creatable && newValue && !localOptions.find(option => option.id === newValue.id)) {
      const createdOption: Option = {
        id: newValue.id,
        value: newValue.value.replace(/Agregar "(.*)"/, '$1')
      };
      setLocalOptions(prev => [...prev, createdOption]);
      setValue(createdOption);
      onChange?.(createdOption.value, createdOption.id);
      return;
    }

    if (!newValue) {
      setValue(null);
      onChange?.('', '');
      return;
    }

    setValue(newValue);
    onChange?.(newValue.value, newValue.id);
  }, [creatable, localOptions, onChange]);

  useEffect(() => {
    const newSelectedOption = selectedOption || null;

    if (newSelectedOption?.id !== _value?.id) {
      setValue(newSelectedOption);
    }
  }, [selectedOption, _value?.id]);

  useEffect(() => {
    const option = options.find(option => option.value === value || option.id === value);
    if (option) {
      setValue(option);
    }
  }, [value, options]);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const popupIcon = useMemo(() => {
    if (error && warning && !open) {
      return (
          <Image
            src="/assets/icons/alert-circle.svg"
            alt={"Icono advertencia"}
            width="16"
            height="16" />
      )
    }
    return (
    <div style={{
      ...popupIconStyles,
      transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
    }}>
      <UiArrowIcon />
    </div>
  )}, [open,error]);

  const isOptionEqualToValue = useCallback((option: Option, value: Option) => {
    if (!option || !value) return false;
    return option.id === value.id;
  }, []);

  const getOptionLabel = useCallback((option: Option | null) => option?.value ?? '', []);

  const renderOption = useCallback((props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key }, option: Option, { selected }: { selected: boolean }) => (
    <li
      {...props}
      key={props.key || option.id}
      style={{
        minHeight: OPTION_MIN_HEIGHT,
        padding: '8px 16px',
        opacity: option.disabled ? 0.5 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: option.disabled ? 'not-allowed' : 'pointer',
        whiteSpace: 'normal',
        wordBreak: 'break-word'
      }}
    >
      <Typography
        component="span"
        sx={{
          flex: 1,
          fontSize: isAlta ? '16px' : '14px',
          lineHeight: '20px',
          color: '#344054',
          whiteSpace: 'normal',
          wordBreak: 'break-word'
        }}
      >
        {option.value}
      </Typography>
      {selected && (
        <UIIcon
          icon={iconName.check}
          size={20}
          color="#F98207"
          style={{ marginLeft: 8, flexShrink: 0 }}
        />
      )}
    </li>
  ), [isAlta]);

  const inputStyles = useMemo(() => ({
    height: COMPONENT_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    '& .MuiInputBase-input': {
      padding: '0 0px 0 8px !important',
      height: `${COMPONENT_HEIGHT} !important`,
      fontSize: isAlta ? '17px' : '14px',
      color: '#344054',
      display: 'flex',
      alignItems: 'center',
      minWidth: '110% !important',
    },
    '& .MuiAutocomplete-endAdornment': {
      position: 'absolute',
      right: '2px',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      '& .MuiButtonBase-root': {
        height: '100%',
        padding: '0 1px',
        minWidth: '20px'
      },
      '& .MuiAutocomplete-clearIndicator': {
        padding: '1px',
        fontSize: '18px'
      }
    }
  }), [isAlta]);

  const textFieldStyles = useMemo(() => ({
    '& .MuiOutlinedInput-root': {
      height: COMPONENT_HEIGHT,
      padding: '0',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
       borderColor: error && warning ? colors.brand100 : colors.primaryAserta,
        boxShadow: error && warning
          ? `0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px ${alpha(colors.brand100, 0.24)}`
          : `0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(249, 130, 7, 0.24)`, 
      },
      '& .MuiOutlinedInput-notchedOutline': {
        //borderColor: error ? colors.primaryAserta : colors.gray300,
        borderRadius: '8px',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        //borderColor: error ? colors.primaryAserta : colors.gray100,
        border: "1px solid",
      },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: error && warning ? colors.brand100 : colors.error500,
      },
      '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: warning && error ? colors.brand100 : colors.error500,
        boxShadow: warning && error
          ? `0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px ${alpha(colors.brand100, 0.24)}`
          : `0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(249, 130, 7, 0.24)`,
      }
    }
  }), [error, warning]);

  const filter = createFilterOptions<any>();

  const filterOptions = useCallback((options: any[], params: FilterOptionsState<any>) => {
    const filtered = filter(options, params);

    const { inputValue } = params;
    // Suggest the creation of a new value
    const isExisting = options.some((option) => inputValue === option.value);
    if (creatable && inputValue !== '' && !isExisting) {
      filtered.push({
        id: inputValue,
        value: `Agregar "${inputValue}"`,
      });
    }

    return filtered;
  }, [creatable, filter]);

  return (
    <FormControl
      fullWidth
      variant="standard"
      error={error && !warning}
      required={required}
    >
      <FormHelperText
        className={`label_form ${sfUIDisplayMedium.className}`}
        sx={labelStyles}
      >
        {label}
      </FormHelperText>

      <Autocomplete
        value={_value}
        onChange={(_, newValue) => handleChange(newValue)}
        disabled={disabled}
        options={filteredOptions}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        disablePortal
        popupIcon={popupIcon}
        renderOption={renderOption}
        filterOptions={filterOptions}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            error={error}
            helperText={errorText}
            InputProps={{
              ...params.InputProps,
              sx: inputStyles
            }}
            sx={textFieldStyles}
          />
        )}
        ListboxProps={{
          style: {
            padding: '4px',
            maxHeight: LISTBOX_MAX_HEIGHT
          }
        }}
        noOptionsText="No se encontraron opciones"
      />
    </FormControl>
  );
};

export default UISelectSearch;